import Decimal from 'decimal.js';
import { AllocateCreditSourceAction, InvoiceSummaryForAllocation } from './invoice.allocation';
import { InvoiceType } from './invoicing';

/** One document the open one can allocate against: a credit for an invoice, or an invoice for a credit. */
export interface FinancialDocumentAllocationCandidate {
    _id: string;
    invoiceNumber: string;
    invoiceDate: Date | string;
    type: InvoiceType;
    total: number;
    /** Unsigned: credit left on a credit note / prepayment, or the amount due on an invoice. */
    available: number;
    updatedAt: Date | string;
    timezone?: string;
}

/** Opening Allocate Credit: the direction and every candidate, read fresh by the server. */
export interface FinancialDocumentAllocationPreflight {
    direction: AllocateCreditSourceAction;
    customerName: string | null;
    documentNumber: string;
    /** The open document's stamp; allocating refuses if it moved since the dialog opened. */
    updatedAt: Date | string;
    /** Unsigned: what the open document can still take (invoice due) or give (remaining credit). */
    available: number;
    candidates: FinancialDocumentAllocationCandidate[];
}

export interface FinancialDocumentAllocationRow {
    _id: string;
    updatedAt: Date | string;
    amount: number;
}

/** The browser names documents and amounts only; every figure the pipeline checks is read from the database. */
export interface FinancialDocumentAllocateRequest {
    documentId: string;
    updatedAt: Date | string;
    rows: FinancialDocumentAllocationRow[];
}

/** A normal invoice or bill receives credit; a credit note or prepayment hands it out. */
export function allocationDirectionFor(type: InvoiceType | undefined, isBill: boolean): AllocateCreditSourceAction {

    const normal = isBill ? InvoiceType.Bill : InvoiceType.Invoice;
    return type === normal ? AllocateCreditSourceAction.AllocateTo : AllocateCreditSourceAction.AllocateFrom;
}

export function toAllocationCandidate(summary: InvoiceSummaryForAllocation): FinancialDocumentAllocationCandidate {

    return {
        _id: summary._id,
        invoiceNumber: summary.invoiceNumber,
        invoiceDate: summary.invoiceDate,
        type: summary.type,
        total: Math.abs(summary.type === InvoiceType.PrepaymentInvoice || summary.type === InvoiceType.PrepaymentSupplier ? summary.prepaymentTotal ?? summary.total : summary.total),
        available: Math.abs(summary.outstandingAmount ?? 0),
        updatedAt: summary.updatedAt,
        timezone: summary.timezone,
    };
}

export type CreditAllocationDecision =
    | { ok: true; rows: Array<{ candidate: InvoiceSummaryForAllocation; amount: number }> }
    | { ok: false; reason: string };

function sameStamp(left: Date | string | undefined, right: Date | string | undefined): boolean {

    return !!left && !!right && new Date(left).getTime() === new Date(right).getTime();
}

const cents = (value: number) => new Decimal(value ?? 0).mul(100).toDecimalPlaces(0);

/**
 * Checks a request against the database's own candidates before the allocation pipeline runs. The
 * pipeline's per-row cap reads the amount it is handed, so the rows passed on are rebuilt from here.
 */
export function decideCreditAllocation(source: Pick<InvoiceSummaryForAllocation, '_id' | 'updatedAt' | 'outstandingAmount'> | null, candidates: InvoiceSummaryForAllocation[] | null, request: FinancialDocumentAllocateRequest): CreditAllocationDecision {

    if (!source?._id || !candidates) return { ok: false, reason: 'Only an approved document can allocate credit' };
    if (!sameStamp(source.updatedAt, request.updatedAt)) return { ok: false, reason: 'This document has changed. Reload it and try again.' };

    // Each row is capped against the document's available amount on its own, and the pipeline updates
    // every row's balance from the same starting document, so a repeated document would over-allocate.
    const ids = (request.rows ?? []).map(row => row._id);
    if (new Set(ids).size !== ids.length) return { ok: false, reason: 'Each document can be credited only once per allocation' };

    const byId = new Map(candidates.map(candidate => [candidate._id?.toString(), candidate]));
    const rows: Array<{ candidate: InvoiceSummaryForAllocation; amount: number }> = [];
    for (const row of request.rows ?? []) {
        const amount = Number(row.amount);
        if (!Number.isFinite(amount) || amount < 0) return { ok: false, reason: 'Amounts to credit cannot be negative' };
        if (amount === 0) continue;
        const candidate = byId.get(row._id);
        if (!candidate) return { ok: false, reason: 'One of the documents can no longer be allocated. Reload and try again.' };
        if (!sameStamp(candidate.updatedAt, row.updatedAt)) return { ok: false, reason: `${candidate.invoiceNumber} has changed. Reload and try again.` };
        if (cents(amount).greaterThan(cents(Math.abs(candidate.outstandingAmount)))) return { ok: false, reason: `${candidate.invoiceNumber} has only ${Math.abs(candidate.outstandingAmount).toFixed(2)} available` };
        rows.push({ candidate, amount });
    }

    if (!rows.length) return { ok: false, reason: 'Enter an amount to credit' };
    const total = rows.reduce((sum, row) => sum.plus(cents(row.amount)), new Decimal(0));
    const limit = cents(Math.abs(source.outstandingAmount));
    if (total.greaterThan(limit)) return { ok: false, reason: `The total to credit cannot exceed ${limit.div(100).toFixed(2)}` };
    return { ok: true, rows };
}
